import { env } from 'next-runtime-env';

import { ProductConfiguration, TotalStepType } from '@storefront/types/Product';
import {
  BundleItem,
  BundleItemOption,
  BundleProduct,
  Maybe,
} from '@storefront/data-commerce';
import {
  getProductImage,
  getProductSmallImage,
} from '@storefront/utils/prodRequests';

import { configurationState } from '../state-management/pdp-config-state';
import { saveProductConfiguration } from '../utils/utils';

export interface BundleItemOptionWithLowestPrice extends BundleItemOption {
  lowestPrice: number;
}
export interface BundleItemWithLowestPrice extends BundleItem {
  options?: Maybe<Array<Maybe<BundleItemOptionWithLowestPrice>>>;
}
export interface BundleProductWithLowestPrice extends BundleProduct {
  items?: Maybe<Array<Maybe<BundleItemWithLowestPrice>>>;
  lowestPrice: number;
}
export interface BundlePcProductWithLowestPrice extends Maybe<BundleProduct> {
  items?: Maybe<Array<Maybe<BundleItemWithLowestPrice>>>;
  lowestPrice: number;
}

export const getBundleProduct = (
  multipleProduct: BundleProductWithLowestPrice[],
  activeSKU: string
) =>
  multipleProduct?.find(
    (product: BundleProduct) => product.sku === activeSKU
  ) ?? multipleProduct?.[0];

export const isSelectedItemsOutOfStock = (
  items: BundleProductWithLowestPrice[],
  selectedSku: string
): boolean =>
  Boolean(
    items?.find(
      (item) =>
        item?.sku === selectedSku && item?.stock_status === 'OUT_OF_STOCK'
    )
  );

export const handleOutOfStock = (
  selectedSku: string,
  items: BundleProductWithLowestPrice[],
  configuration: ProductConfiguration
) => {
  if (selectedSku && isSelectedItemsOutOfStock(items, selectedSku)) {
    const newState = {
      ...configuration,
      activeSku: undefined as string | undefined,
    };
    configurationState(newState);
    saveProductConfiguration(newState);
  }
};

export const getProductLevelImage = (
  activeSKU: string,
  bundleProduct: BundleProductWithLowestPrice,
  smallImage: boolean,
  isSMB: boolean
) => {
  let noSelectionImage;
  if (isSMB) {
    noSelectionImage = smallImage
      ? env('NEXT_PUBLIC_NO_SELECTION_URL_SMALLER_DEVICE_SMB')
      : env('NEXT_PUBLIC_NO_SELECTION_URL_DESKTOP_SMB');
  } else {
    noSelectionImage = smallImage
      ? env('NEXT_PUBLIC_NO_SELECTION_URL_SMALLER_DEVICE')
      : env('NEXT_PUBLIC_NO_SELECTION_URL_DESKTOP');
  }
  const imageSizeHandler = smallImage
    ? getProductSmallImage(bundleProduct?.items?.[0])
    : getProductImage(bundleProduct?.items?.[0]);
  return activeSKU ? imageSizeHandler : noSelectionImage;
};

export const calculateSteps = (steps: TotalStepType) => {
  const { coreOfferTotalSteps: coreOfferSteps, simpleTotalSteps } = steps;
  const hybridTotalSteps = coreOfferSteps + simpleTotalSteps + 1;

  const regularAndBundleTotalSteps = coreOfferSteps + simpleTotalSteps;

  const coreOfferTotalSteps = coreOfferSteps + 1;

  return {
    coreOfferTotalSteps,
    hybridTotalSteps,
    regularAndBundleTotalSteps,
  };
};

export const scrollToElement = (elementId: string, delay: number = 400) => {
  const element = document.getElementById(elementId);
  if (!element) return;

  setTimeout(() => {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, delay);
};

export const scrollToEndOfElement = ({
  elementId,
  delay = 400,
  offsetHeader,
}: {
  elementId: string;
  delay?: number;
  offsetHeader?: boolean;
}) => {
  const element = document.getElementById(elementId);
  if (!element) return;

  const headerHeight = offsetHeader ? 56 : 0;
  const bottom = element.offsetTop + element.offsetHeight - headerHeight;
  setTimeout(() => {
    window.scrollTo({ top: bottom, behavior: 'smooth' });
  }, delay);
};

interface IncludedOptionInterface {
  isIncluded: boolean;
  price: number;
}

export const handlePriceDisplay = (
  bundleProduct: BundleProductWithLowestPrice,
  option: BundleItemOptionWithLowestPrice
): IncludedOptionInterface => {
  const lowestPrice = bundleProduct?.lowest_price;

  const isLowestOption = option?.lowest_price === lowestPrice;

  return {
    isIncluded: isLowestOption,
    price: option.lowest_price,
  };
};
